import { Injectable } from '@angular/core';
import { environment } from '../../environment/environment';
declare var clevertap: any;
@Injectable({ providedIn: 'root' })
export class ClevertapService {
    private isInitialized = false;
    constructor() {
        this.initializeCleverTap();
    }
    private initializeCleverTap(): void {
        if (typeof clevertap !== 'undefined' && clevertap.event) {
            this.ensureAccountSet();
            this.isInitialized = true;
            console.log('CleverTap initialized successfully');
            this.registerServiceWorker();
        } else {
            console.warn('CleverTap SDK not loaded. Retrying...');
            setTimeout(() => this.initializeCleverTap(), 1000);
        }
    }

    private setupNotificationCallback(): void {
        if (typeof clevertap !== 'undefined') {
            clevertap.notificationCallback = (msg: any) => {
                console.log('CleverTap: Web Pop-up received:', JSON.stringify(msg));

                // Track viewed event automatically
                if (msg && msg.msgId) {
                    clevertap.renderNotificationViewed({ msgId: msg.msgId });
                    console.log('CleverTap: Raised notification viewed event for:', msg.msgId);
                }
            };
        }
    }

    private ensureAccountSet(): void {
        if (typeof clevertap !== 'undefined') {
            if (!clevertap.account || clevertap.account.length === 0) {
                console.log('Restoring CleverTap Account ID at runtime from environment');
                clevertap.account = [{ id: environment.CLEVERTAP_ACCOUNT_ID }];
                clevertap.region = environment.CLEVERTAP_REGION;
                clevertap.enablePersonalization = true;
                clevertap.spa = true;
            } else {
                console.log('CleverTap Account ID is already set correctly:', JSON.stringify(clevertap.account));
            }
        }
    }

    private registerServiceWorker(): void {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/clevertap_sw.js')
                .then(reg => console.log('Service Worker registered successfully:', reg))
                .catch(err => console.error('Service Worker registration failed:', err));
        } else {
            console.warn('Service workers are not supported in this browser');
        }
    }
    trackEvent(name: string, data: any = {}): void {
        try {
            if (typeof clevertap !== 'undefined') {
                this.ensureAccountSet();
                const userUuid = this.getUserUUID();
                const userDetails = this.getUserDetailsLocal();
                const eventData = { ...userDetails, ...data, user_uuid: userUuid };

                console.log(`Tracking CleverTap event: ${name}`, eventData);
                clevertap.event.push(name, eventData);
            } else {
                console.warn('CleverTap SDK not available for tracking event:', name);
            }
        } catch (error) {
            console.error('Error tracking CleverTap event:', name, error);
        }
    }
    setUserProfile(email: string, firstName: string, lastName: string, phoneNumber: string, name: string, uuid?: string): void {
        try {
            if (typeof clevertap !== 'undefined' && clevertap.onUserLogin) {
                // Format phone number correctly for CleverTap: +[country_code][number]
                let formattedPhone = phoneNumber;
                if (phoneNumber && !phoneNumber.startsWith('+')) {
                    // Assuming default country code is 84 if not present
                    formattedPhone = phoneNumber.startsWith('84') ? `+${phoneNumber}` : `+84${phoneNumber.replace(/^0/, '')}`;
                }

                const profile: any = {
                    'Name': name || `${firstName} ${lastName}`,
                    'Email': email,
                    'Phone': formattedPhone,
                    'Identity': uuid || email || formattedPhone || this.getUserUUID(),
                    'First Name': firstName,
                    'Last Name': lastName
                };
                if (uuid) {
                    profile['UUID'] = uuid;
                }
                console.log('Setting CleverTap user profile:', profile);
                clevertap.onUserLogin.push({ "Site": profile });
            } else {
                console.warn('CleverTap SDK not available for setting user profile');
            }
        } catch (error) {
            console.error('Error setting CleverTap user profile:', error);
        }
    }
    setUserAttribute(key: string, value: any): void {
        try {
            if (typeof clevertap !== 'undefined') {
                const profileUpdate: any = {};
                profileUpdate[key] = value;
                clevertap.profile.push({ "Site": profileUpdate });
            } else {
                console.warn('CleverTap SDK not available for setting user attribute:', key);
            }
        } catch (error) {
            console.error('Error setting CleverTap user attribute:', key, error);
        }
    }

    showNotificationPrompt(): void {
        const permission = (window as any).Notification ? (window as any).Notification.permission : 'default';

        console.log("CleverTap: Checking status before prompt...");
        console.log("  - Account ID exists:", !!(clevertap && clevertap.account && clevertap.account.length));
        console.log("  - Notification permission:", permission);
        console.log("  - Current Domain:", window.location.origin);
        console.log("  - Personalization Active:", typeof clevertap._isPersonalisationActive === 'function' ? clevertap._isPersonalisationActive() : 'unknown');

        if (permission === 'granted') {
            console.log('CleverTap: User already subscribed, skipping soft prompt dialogue.');
            return;
        }

        try {
            this.ensureAccountSet();
            if (typeof clevertap !== 'undefined' && clevertap.notifications) {
                console.log('CleverTap: Prompting for push notifications');
                clevertap.notifications.push({
                    "titleText": "Would you like to receive Push Notifications?",
                    "bodyText": "We promise to only send you relevant content and give you updates on your transactions",
                    "okButtonText": "Yes",
                    "rejectButtonText": "No",
                    "okButtonColor": "#F5375C",
                    "askAgainTimeInSeconds": 5,
                    "serviceWorkerPath": "/clevertap_sw.js"
                });
            } else {
                console.warn('CleverTap SDK or notifications not available');
            }
        } catch (error) {
            console.error('Error showing CleverTap notification prompt:', error);
        }
    }

    onUserLoginIdentity(uuid: string): void {
        try {
            if (typeof clevertap !== 'undefined' && clevertap.onUserLogin) {
                console.log('Identifying CleverTap user with UUID:', uuid);
                clevertap.onUserLogin.push({
                    "Site": {
                        "Identity": uuid
                    }
                });
            } else {
                console.warn('CleverTap SDK not available for user login identification');
            }
        } catch (error) {
            console.error('Error in CleverTap onUserLoginIdentity:', error);
        }
    }

    getUserUUID(): string {
        let uuid = localStorage.getItem('user_uuid');
        if (!uuid) {
            uuid = this.generateUUID();
            localStorage.setItem('user_uuid', uuid);
        }
        return uuid;
    }

    saveUserDetails(details: any): void {
        if (details) {
            // Prioritize server-provided UUID
            if (details.uuid) {
                localStorage.setItem('user_uuid', details.uuid);
            }

            const dataToSave = {
                user_email: details.email,
                user_name: details.name,
                user_phone: details.mobileNo || details.phoneNumber,
                customer_id: `${details.countryCode || '84'}-${details.mobileNo || details.phoneNumber}`
            };
            localStorage.setItem('user_profile_details', JSON.stringify(dataToSave));
        }
    }

    getUserDetailsLocal(): any {
        const details = localStorage.getItem('user_profile_details');
        return details ? JSON.parse(details) : {};
    }

    clearStorage(): void {
        localStorage.removeItem('user_uuid');
        localStorage.removeItem('user_profile_details');
        // Proactively generate a new anonymous UUID and notify SDK
        const newUuid = this.getUserUUID();
        this.onUserLoginIdentity(newUuid);
    }

    private generateUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}
